import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { authApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { Field, useCurrentUser } from '@/features/auth'
import { useT, useTx } from '@/i18n'

/** 与后端 REACTIVATION_DAYS 一致；真正的天数以接口返回为准，这里只用于提交前的说明 */
const REACTIVATION_DAYS = 30

export function SettingsDeactivatePage() {
  const t = useT()
  const tx = useTx()
  const { user } = useCurrentUser()
  const [password, setPassword] = useState('')
  const client = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()

  const deactivate = useMutation({
    mutationFn: () => authApi.deactivate(password),
    onSuccess: (result) => {
      client.setQueryData(keys.me(), null)
      client.clear()
      navigate('/login', { replace: true })
      toast.show(t('deactivate.done', { days: result.reactivation_days }))
    },
  })
  const error = deactivate.error instanceof ApiError ? deactivate.error : null

  if (!user) return null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    deactivate.mutate()
  }

  return (
    <>
      <PageHeader title={t('deactivate.title')} back />
      <div className="flex items-center gap-3 border-b border-line px-4 py-4">
        <Avatar src={user.avatar_url} alt={user.display_name} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-[15px] font-bold">{user.display_name}</p>
          <p className="truncate text-[15px] text-ink-muted">@{user.username}</p>
        </div>
      </div>

      <section className="space-y-3 border-b border-line px-4 py-4 text-[15px]">
        <h2 className="text-lg font-extrabold">{t('deactivate.whatHappens')}</h2>
        <ul className="list-disc space-y-2 pl-5">
          <li>{t('deactivate.point1')}</li>
          <li>
            {tx('deactivate.point2', {
              days: <strong>{REACTIVATION_DAYS}</strong>,
            })}
            {t('deactivate.point2Rest')}
          </li>
          <li>{t('deactivate.point3', { days: REACTIVATION_DAYS, username: user.username })}</li>
          <li>{t('deactivate.point4')}</li>
        </ul>
        <p className="text-ink-muted">
          {tx('deactivate.exportHint', {
            link: (
              <Link to="/settings/data" className="text-brand-ink underline">
                {t('deactivate.exportLink')}
              </Link>
            ),
          })}
        </p>
        <p className="text-ink-muted">{t('deactivate.renameHint')}</p>
      </section>

      <form onSubmit={onSubmit} className="max-w-sm space-y-3 px-4 py-4" noValidate>
        <Field
          label={t('deactivate.confirmLabel')}
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error?.message}
          required
        />
        <Button type="submit" variant="danger" loading={deactivate.isPending} disabled={!password}>
          {t('deactivate.title')}
        </Button>
      </form>
    </>
  )
}
